import React, { useEffect, useState, useCallback } from 'react';
import { superAdminService } from '../../services/api';

interface ArchivedTenant {
    tenantPk: string;
    tier: string | null;
    archivedAt: string | null;
    archivedReason: string | null;
    totalItems: number;
    propertyCount: number;
    certificateCount: number;
    evidenceCount: number;
}

const ArchivedTenantsTab: React.FC = () => {
    const [tenants, setTenants] = useState<ArchivedTenant[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyPk, setBusyPk] = useState<string | null>(null);
    const [restoreState, setRestoreState] = useState<{ pk: string; email: string } | null>(null);
    const [confirmDeletePk, setConfirmDeletePk] = useState<string | null>(null);
    const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await superAdminService.getArchivedTenants();
            const data = (res.data as any)?.archived ?? [];
            setTenants(data);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load archived tenants');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 5000);
            return () => clearTimeout(t);
        }
    }, [toast]);

    const handleHardDelete = async (pk: string) => {
        setBusyPk(pk);
        try {
            await superAdminService.hardDeleteArchivedTenant(pk);
            setToast({ kind: 'success', message: `Hard-deleted ${pk}` });
            setConfirmDeletePk(null);
            await load();
        } catch (err: any) {
            setToast({ kind: 'error', message: err?.response?.data?.message || 'Delete failed' });
        } finally {
            setBusyPk(null);
        }
    };

    const handleRestore = async (pk: string, email: string) => {
        if (!email || !email.includes('@')) {
            setToast({ kind: 'error', message: 'Enter a valid email address.' });
            return;
        }
        setBusyPk(pk);
        try {
            const res = await superAdminService.restoreArchivedTenant(pk, email);
            const data: any = res.data;
            setToast({ kind: 'success', message: `Restored as ${email}. Cognito sub: ${data?.cognitoSub || '(unknown)'}` });
            setRestoreState(null);
            await load();
        } catch (err: any) {
            setToast({ kind: 'error', message: err?.response?.data?.message || 'Restore failed' });
        } finally {
            setBusyPk(null);
        }
    };

    const formatDate = (iso: string | null) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
        } catch {
            return iso;
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-zinc-900">Archived Tenants</h2>
                    <p className="text-sm text-zinc-600 mt-1 max-w-3xl">
                        Tenants with DynamoDB property/certificate data but no matching Cognito user. Marked as archived to keep them out of active dashboards.
                        Use <strong>Restore</strong> to create a fresh Cognito user linked to the existing data (the user is emailed a password-reset link), or <strong>Hard Delete</strong> to permanently purge everything.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="px-3 py-2 text-sm bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-50"
                >
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            {toast && (
                <div className={`p-3 rounded-lg text-sm ${toast.kind === 'success' ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' : 'bg-red-50 text-red-900 border border-red-200'}`}>
                    {toast.message}
                </div>
            )}

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-900 text-sm">{error}</div>
            )}

            {!loading && !error && tenants.length === 0 && (
                <div className="p-8 bg-zinc-50 border border-zinc-200 rounded-lg text-center text-zinc-500">
                    No archived tenants. Every active subscription has a matching Cognito user.
                </div>
            )}

            {tenants.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
                    <table className="min-w-full text-sm">
                        <thead className="bg-zinc-50 text-zinc-700">
                            <tr>
                                <th className="px-4 py-3 text-left font-semibold">Tenant PK</th>
                                <th className="px-4 py-3 text-left font-semibold">Tier</th>
                                <th className="px-4 py-3 text-right font-semibold">Props</th>
                                <th className="px-4 py-3 text-right font-semibold">Certs</th>
                                <th className="px-4 py-3 text-right font-semibold">Total Items</th>
                                <th className="px-4 py-3 text-left font-semibold">Archived</th>
                                <th className="px-4 py-3 text-left font-semibold">Reason</th>
                                <th className="px-4 py-3 text-right font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                            {tenants.map((t) => (
                                <tr key={t.tenantPk} className="hover:bg-zinc-50">
                                    <td className="px-4 py-3 font-mono text-xs text-zinc-700 break-all" title={t.tenantPk}>
                                        {t.tenantPk.replace(/^TENANT#TENANT_/, '').slice(0, 16)}…
                                    </td>
                                    <td className="px-4 py-3 text-zinc-700">{t.tier || '—'}</td>
                                    <td className="px-4 py-3 text-right text-zinc-700">{t.propertyCount}</td>
                                    <td className="px-4 py-3 text-right text-zinc-700">{t.certificateCount}</td>
                                    <td className="px-4 py-3 text-right text-zinc-900 font-semibold">{t.totalItems}</td>
                                    <td className="px-4 py-3 text-xs text-zinc-600">{formatDate(t.archivedAt)}</td>
                                    <td className="px-4 py-3 text-xs text-zinc-600 max-w-xs truncate" title={t.archivedReason || ''}>
                                        {t.archivedReason || '—'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => setRestoreState({ pk: t.tenantPk, email: '' })}
                                                disabled={busyPk === t.tenantPk}
                                                className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                                            >
                                                Restore
                                            </button>
                                            <button
                                                onClick={() => setConfirmDeletePk(t.tenantPk)}
                                                disabled={busyPk === t.tenantPk}
                                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                            >
                                                Hard Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Restore modal */}
            {restoreState && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setRestoreState(null)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-bold mb-2">Restore Archived Tenant</h3>
                        <p className="text-sm text-zinc-600 mb-4">
                            A fresh Cognito user will be created with the email below, linked to this tenant's existing DynamoDB data. Cognito will email them a temporary password.
                        </p>
                        <p className="text-xs font-mono bg-zinc-100 p-2 rounded mb-3 break-all">{restoreState.pk}</p>
                        <input
                            type="email"
                            placeholder="user@example.com"
                            value={restoreState.email}
                            onChange={(e) => setRestoreState({ ...restoreState, email: e.target.value })}
                            className="w-full border border-zinc-300 rounded px-3 py-2 mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setRestoreState(null)} className="px-4 py-2 text-sm border border-zinc-300 rounded hover:bg-zinc-50">
                                Cancel
                            </button>
                            <button
                                onClick={() => handleRestore(restoreState.pk, restoreState.email)}
                                disabled={busyPk === restoreState.pk}
                                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {busyPk === restoreState.pk ? 'Restoring…' : 'Create Cognito user & email'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hard-delete confirmation modal */}
            {confirmDeletePk && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDeletePk(null)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-bold mb-2 text-red-700">Permanently Delete Tenant Data?</h3>
                        <p className="text-sm text-zinc-700 mb-4">
                            This will <strong>permanently delete every DynamoDB item</strong> under this tenant's partition key — properties, certificates, evidence, settings, everything. Cannot be undone.
                        </p>
                        <p className="text-xs font-mono bg-red-50 border border-red-200 p-2 rounded mb-4 break-all">{confirmDeletePk}</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setConfirmDeletePk(null)} className="px-4 py-2 text-sm border border-zinc-300 rounded hover:bg-zinc-50">
                                Cancel
                            </button>
                            <button
                                onClick={() => handleHardDelete(confirmDeletePk)}
                                disabled={busyPk === confirmDeletePk}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                                {busyPk === confirmDeletePk ? 'Deleting…' : 'Yes, permanently delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArchivedTenantsTab;
